import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppContext } from './context/AppContext';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import DomainGate from './components/routing/DomainGate';

// Public Pages
import Home from './pages/Home';
import Auth from './pages/Auth';
import Welcome from './pages/Welcome';
import PublicProfile from './pages/PublicProfile';
import DeviceRedirect from './pages/DeviceRedirect';
import Pricing from './pages/Pricing';

// Store Pages
import StoreList from './pages/Store/StoreList';
import ProductDetail from './pages/Store/ProductDetail';
import Cart from './pages/Store/Cart';

// Dashboard Pages
import Overview from './pages/Dashboard/Overview';
import EditProfile from './pages/Dashboard/EditProfile';
import Devices from './pages/Dashboard/Devices';
import Orders from './pages/Dashboard/Orders';
import CustomDomain from './pages/Dashboard/CustomDomain';
import DashboardLayout from './components/layout/DashboardLayout';

// Admin Pages
import ManageProducts from './pages/Dashboard/Admin/ManageProducts';
import ManageDevices from './pages/Dashboard/Admin/ManageDevices';
import ManageUsers from './pages/Dashboard/Admin/ManageUsers';
import ManageOrders from './pages/Dashboard/Admin/ManageOrders';
import ManageRestrictedDomains from './pages/Dashboard/Admin/ManageRestrictedDomains';
import AdminSettings from './pages/Dashboard/Admin/Settings';

/**
 * Protected Route Wrapper
 * Loading state is kept invisible (returns null)
 */
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, profile, loading } = useAppContext();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !profile?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default function App() {
  return (
    <DomainGate>
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/p/:username" element={<PublicProfile />} />
            <Route path="/d/:serial" element={<DeviceRedirect />} />

            {/* Store Routes */}
            <Route path="/store" element={<StoreList />} />
            <Route path="/store/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />

            {/* User Dashboard */}
            <Route path="/dashboard" element={<ProtectedRoute><Overview /></ProtectedRoute>} />
            <Route path="/dashboard/profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
            <Route path="/dashboard/devices" element={<ProtectedRoute><Devices /></ProtectedRoute>} />
            <Route path="/dashboard/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/dashboard/domain" element={<ProtectedRoute><DashboardLayout><CustomDomain /></DashboardLayout></ProtectedRoute>} />

            {/* Admin Dashboard */}
            <Route path="/dashboard/admin/products" element={<ProtectedRoute adminOnly><ManageProducts /></ProtectedRoute>} />
            <Route path="/dashboard/admin/devices" element={<ProtectedRoute adminOnly><ManageDevices /></ProtectedRoute>} />
            <Route path="/dashboard/admin/users" element={<ProtectedRoute adminOnly><ManageUsers /></ProtectedRoute>} />
            <Route path="/dashboard/admin/orders" element={<ProtectedRoute adminOnly><ManageOrders /></ProtectedRoute>} />
            <Route path="/dashboard/admin/restrictions" element={<ProtectedRoute adminOnly><ManageRestrictedDomains /></ProtectedRoute>} />
            <Route path="/dashboard/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </DomainGate>
  );
}
